import { SagaIterator } from 'redux-saga'
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects'

import { ItemProveedor, StateReservasHistorico, DateFilter } from './ReservasHistoricoRedux'
import {
  GET_HISTORY_RESERVAS,
  GET_PROVEEDORES_RESERVAS,
  LOADING_HISTORICO_RESERVAS,
  LOADING_PROVEEDORES_RESERVAS,
  SET_HISTORY_RESERVAS,
  SET_PROVEEDORES_RESERVAS,
} from './type'

import { RootState } from '../../store'

import { urlHistoricoReserva, urlHistoricoReservaCentralizada } from '@/common/constants/api'
import { get } from '@/common/helpers/api_helper'
import { notifications } from '@/common/helpers/notifications'
import { getProveedoresReservas } from '@/common/helpers/odataApiServices'
import { esObjetoVacio } from '@/common/helpers/utils'
import { dateTimeToDateForms, dateTimeToDateFormsFinExclusivo, toDate } from '@/common/helpers/utilsDate'
import { IBookingsState, IItemBooking } from '@/common/interfaces/booking'

interface IResultadoHistorico {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[]
  total: number
}

const Booking = (state: RootState): IBookingsState => state.Booking
const ReservasHistorico = (state: RootState): StateReservasHistorico => state.ReservasHistorico

const getReservas = (filtros: string, tipoReserva: string): Promise<IResultadoHistorico | void> => {
  const replacedURL = tipoReserva === 'ReservaCentralizada' ? urlHistoricoReservaCentralizada : urlHistoricoReserva

  // Un solo esquema de paginación para los dos tipos de reserva: se trae el conjunto
  // acotado y el grid pagina/ordena/filtra en cliente con process(). Mandar el
  // $skip/$top del dataState dejaba el total del grid en el largo de la página, así
  // que el paginador informaba una sola página y el resto quedaba inalcanzable.
  const queryParams = `&$filter=${filtros}&$top=5000`

  return get(`${replacedURL}${queryParams}`)
    .then((res) => {
      if (res.value.length === 0) {
        notifications({
          title: 'Información',
          message: 'No se encontraron registros.',
          toastType: 'info',
        })
      }

      return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: res.value.map((item: any) => {
          item.FechaCreacion = toDate(item.FechaCreacion)
          item.FechaUltimaModificacion = toDate(item.FechaUltimaModificacion)
          if (item.ReservaCentralizada) {
            item.ReservaCentralizada.FechaInicio = toDate(item.ReservaCentralizada.FechaInicio)
            item.ReservaCentralizada.FechaFin = toDate(item.ReservaCentralizada.FechaFin)
            item.ReservaCentralizada.FechaCreacion = toDate(item.ReservaCentralizada.FechaCreacion)
          } else if (item.Reserva) {
            item.Reserva.FechaCreacion = toDate(item.Reserva.FechaCreacion)
            item.Reserva.FechaInicioProducto = toDate(item.Reserva.FechaInicioProducto)
            item.Reserva.FechaFinProducto = toDate(item.Reserva.FechaFinProducto)
          }

          return item
        }),
        total: res['@odata.count'],
      }
    })
    .catch((error) => {
      notifications({ ex: error })
    })
}

// Exportado para poder verificar en tests la cadena OData que se manda al backend
export const handleFilter = (
  ordenPedido: number,
  proveedorSelect: ItemProveedor,
  selectBookingReserva: IItemBooking,
  tipoReserva: string,
  dateFilter: DateFilter,
): string => {
  const filters: string[] = []

  // Discriminador: cada carta apunta SOLO a uno de los dos modelos de reserva
  // (Reserva legacy o ReservaCentralizada). Sin este filtro la query trae
  // ambos tipos mezclados con el navegador opuesto en null.
  filters.push(`${tipoReserva}Id ne null`)

  if (!esObjetoVacio(proveedorSelect)) {
    filters.push(`${tipoReserva}/Proveedor/ProveedorId eq ${proveedorSelect.ProveedorId}`)
  }

  if (!esObjetoVacio(selectBookingReserva)) {
    filters.push(
      `${tipoReserva}/${tipoReserva === 'ReservaCentralizada' ? 'ActividadMaestra/' : ''}Booking/BookingId eq ${selectBookingReserva.BookingId}`,
    )
  }

  if (ordenPedido > 0) {
    filters.push(`OrdenPedido eq ${ordenPedido}`)
  }

  // Agregar filtros de fecha
  if (dateFilter.startDate && dateFilter.endDate) {
    const startDateFormatted = dateTimeToDateForms(dateFilter.startDate)
    // Los campos comparados guardan hora y el DatePicker entrega la fecha sin hora: con
    // `le fechaFin` la cota superior valía las 00:00 y se perdía todo el último día del
    // rango, así que se compara con `lt` contra el día siguiente.
    const endDateExclusive = dateTimeToDateFormsFinExclusivo(dateFilter.endDate)

    if (dateFilter.filterType === 'reserva') {
      // Filtrar por fecha de inicio: Reserva usa FechaInicioProducto, ReservaCentralizada usa FechaInicio
      const fechaInicioField = tipoReserva === 'Reserva' ? 'FechaInicioProducto' : 'FechaInicio'

      filters.push(
        `${tipoReserva}/${fechaInicioField} ge ${startDateFormatted} and ${tipoReserva}/${fechaInicioField} lt ${endDateExclusive}`,
      )
    } else if (dateFilter.filterType === 'historico') {
      // Filtrar por fecha de creación del registro histórico
      filters.push(`FechaCreacion ge ${startDateFormatted} and FechaCreacion lt ${endDateExclusive}`)
    }
  }

  return filters.join(' and ')
}

function* getProveedores(): SagaIterator {
  try {
    yield put({ type: LOADING_PROVEEDORES_RESERVAS, payload: true })
    const proveedoresR = yield call(getProveedoresReservas)
    const proveedoresFilteres = proveedoresR?.value ?? []

    yield put({ type: SET_PROVEEDORES_RESERVAS, payload: proveedoresFilteres })
  } catch {
    notifications({
      title: 'Error',
      message: 'No se pudieron cargar los proveedores. Intente nuevamente.',
      toastType: 'error',
    })
    yield put({ type: SET_PROVEEDORES_RESERVAS, payload: [] })
  } finally {
    yield put({ type: LOADING_PROVEEDORES_RESERVAS, payload: false })
  }
}

function* getReservasHistorico(): SagaIterator {
  try {
    yield put({ type: LOADING_HISTORICO_RESERVAS, payload: true })
    const { proveedorSelect, ordenPedido, tipoReserva, dateFilter }: StateReservasHistorico =
      yield select(ReservasHistorico)

    const { selectBookingReserva }: IBookingsState = yield select(Booking)
    const filters = handleFilter(ordenPedido, proveedorSelect, selectBookingReserva, tipoReserva, dateFilter)
    const reservas = yield call(getReservas, filters, tipoReserva)

    if (reservas) {
      yield put({ type: SET_HISTORY_RESERVAS, payload: reservas })
    } else {
      yield put({ type: SET_HISTORY_RESERVAS, payload: { data: [], total: 0 } })
    }
  } catch {
    notifications({
      title: 'Error de búsqueda',
      message:
        'No se pudieron cargar los registros históricos. Por favor, verifique los filtros aplicados e intente nuevamente.',
      toastType: 'error',
    })
    yield put({ type: SET_HISTORY_RESERVAS, payload: { data: [], total: 0 } })
  } finally {
    yield put({ type: LOADING_HISTORICO_RESERVAS, payload: false })
  }
}

function* watchGetProveedoresReservas(): Generator {
  yield takeEvery(GET_PROVEEDORES_RESERVAS, getProveedores)
}

function* watchGetHistorialReservas(): Generator {
  yield takeEvery(GET_HISTORY_RESERVAS, getReservasHistorico)
}

export default function* reservasHistoricoSagas(): Generator {
  yield all([fork(watchGetProveedoresReservas), fork(watchGetHistorialReservas)])
}
